import React, { useCallback, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
type Slide = {
  title: string;
  img: string;
  desc: string;
};
const allSlides: Slide[] = [{
  title: 'Golf Communities',
  img: 'https://images.unsplash.com/photo-1535131749006-b7f58c99034b?auto=format&fit=crop&q=80',
  desc: 'Exclusive access to championship courses.'
}, {
  title: 'Hidden Beaches',
  img: 'https://images.unsplash.com/photo-1506929562872-bb421503ef21?auto=format&fit=crop&q=80',
  desc: 'Secret coves and swimmable waters.'
}, {
  title: 'Culinary Scene',
  img: 'https://images.unsplash.com/photo-1551504734-5ee1c4a1479b?auto=format&fit=crop&q=80',
  desc: 'Farm-to-table dining and local flavors.'
}, {
  title: 'Sport Fishing',
  img: 'https://images.unsplash.com/photo-1545566239-0b1bd1bf233a?auto=format&fit=crop&q=80',
  desc: 'Marlin capital of the world.'
}, {
  title: 'Wellness & Retreats',
  img: 'https://images.unsplash.com/photo-1540206395-68808572332f?auto=format&fit=crop&q=80',
  desc: 'World-class spas and oceanfront yoga.'
}, {
  title: 'Yachting & The Marina',
  img: 'https://images.unsplash.com/photo-1540541338287-41700207dee6?auto=format&fit=crop&q=80',
  desc: 'A life lived on the water.'
}, {
  title: 'Whale Watching',
  img: 'https://images.unsplash.com/photo-1568430462989-44163eb1752f?auto=format&fit=crop&q=80',
  desc: 'Witness the great gray whale migration.'
}, {
  title: 'Pickleball & Racquet',
  img: 'https://images.unsplash.com/photo-1622279457486-62dcc4a431d6?auto=format&fit=crop&q=80',
  desc: 'The fastest growing community sport.'
}, {
  title: 'Tequila & Mezcal',
  img: 'https://images.unsplash.com/photo-1514362545857-3bc16c4c7d1b?auto=format&fit=crop&q=80',
  desc: 'A heritage of craft spirits and tasting.'
}];
// Group into pages of 3
const PAGE_SIZE = 3;
const pages: Slide[][] = [];
for (let i = 0; i < allSlides.length; i += PAGE_SIZE) {
  pages.push(allSlides.slice(i, i + PAGE_SIZE));
}
const AUTO_ADVANCE_MS = 7000;
export function LifestyleSlider() {
  const [page, setPage] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const next = useCallback(() => {
    setPage(p => (p + 1) % pages.length);
  }, []);
  const prev = useCallback(() => {
    setPage(p => (p - 1 + pages.length) % pages.length);
  }, []);
  useEffect(() => {
    if (isPaused) return;
    const t = setInterval(next, AUTO_ADVANCE_MS);
    return () => clearInterval(t);
  }, [isPaused, next]);
  const currentPage = pages[page];
  return <div className="relative" onMouseEnter={() => setIsPaused(true)} onMouseLeave={() => setIsPaused(false)} data-id="element-125" data-mp="161jfdttrybsz">
      {/* Cards */}
      <AnimatePresence mode="wait" data-id="element-126" data-mp="2hhzzw1k6rimp">
        <motion.div key={page} initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -20
      }} transition={{
        duration: 0.5
      }} className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-127" data-mp="2hgydx21mtbq7">
          {currentPage.map((item, i) => <div key={`${page}-${i}`} className="group relative h-96 overflow-hidden cursor-pointer" data-id="element-128" data-mp="2f3l3m9tjspet" data-mpi={i}>
              <img src={item.img} alt={item.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" data-id="element-129" data-mp="32u8lnnx6gvf3" />
              <div className="absolute inset-0 bg-gradient-to-t from-navy/90 via-navy/20 to-transparent" data-id="element-130" data-mp="2tzascfq4bgn8" />
              <div className="absolute bottom-0 left-0 p-8" data-id="element-131" data-mp="2y0w3nwn07mvb">
                <h3 className="text-2xl font-serif text-white mb-2" data-id="element-132" data-mp="3lrrno7l7xds0">
                  {item.title}
                </h3>
                <p className="text-sand/80 font-light opacity-0 group-hover:opacity-100 transition-opacity duration-300 transform translate-y-4 group-hover:translate-y-0" data-id="element-133" data-mp="3qkksevgosm43">
                  {item.desc}
                </p>
              </div>
            </div>)}
          {/* Pad with empty slots if last page has fewer than 3 */}
          {Array.from({
          length: PAGE_SIZE - currentPage.length
        }).map((_, i) => <div key={`pad-${i}`} className="hidden md:block h-96" data-id="element-134" data-mp="2llzb9dnbp7fj" data-mpi={i} />)}
        </motion.div>
      </AnimatePresence>

      {/* Controls */}
      <div className="flex items-center justify-between mt-8" data-id="element-135" data-mp="2gmweeghhy7in">
        <div className="flex items-center space-x-3" data-id="element-136" data-mp="2buuv4alwvgj4">
          {pages.map((_, i) => <button key={i} onClick={() => setPage(i)} aria-label={`Go to page ${i + 1}`} className={`h-[2px] transition-all duration-500 ${i === page ? 'w-12 bg-gold' : 'w-6 bg-white/30 hover:bg-white/60'}`} data-id="element-137" data-mp="27ukttb92deal" data-mpi={i} />)}
          <span className="ml-3 text-white/60 text-xs tracking-widest tabular-nums font-light" data-id="element-138" data-mp="m2ukyoktcigc">
            {String(page + 1).padStart(2, '0')} /{' '}
            {String(pages.length).padStart(2, '0')}
          </span>
        </div>

        <div className="flex space-x-2" data-id="element-139" data-mp="v2hobfx3ryc1">
          <button onClick={prev} aria-label="Previous" className="w-11 h-11 flex items-center justify-center border border-white/20 hover:border-gold hover:bg-gold hover:text-navy text-white transition-colors" data-id="element-140" data-mp="v5lxkrpfdk9k">
            <ChevronLeft size={20} data-id="element-141" data-mp="19z8ohrelpe2p" />
          </button>
          <button onClick={next} aria-label="Next" className="w-11 h-11 flex items-center justify-center border border-white/20 hover:border-gold hover:bg-gold hover:text-navy text-white transition-colors" data-id="element-142" data-mp="1eriawxgibw08">
            <ChevronRight size={20} data-id="element-143" data-mp="1nthunxiwpe6v" />
          </button>
        </div>
      </div>
    </div>;
}